import type { ReactNode } from 'react';
import { Button, Caption1, Text, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ShellIcons, shellMedia, shellMetrics, tokens } from '@vigeo/ui';
import { PinLetter } from '@/features/places';
import { complianceAndDocumentsShown } from '@/lib/sections';
import type { StaffHeading } from './coverage.js';
import type { NameSort } from './rosterFilters.js';
import { Compliance, NameLine, PlacesLine, type StaffTableProps } from './StaffTable.js';

/**
 * THE ROSTER SIDE OF THE MAP VIEW (#689; Figma page 12, frames M1, M2, M4b and M5, approved by Julio on 2026-10-12, #648).
 *
 * The roster as a narrow list beside the map: what is at the top (Search and the Filters button, handed in), a heading that
 * says what the list is showing, one compact row a person, and a foot line about the staff the map cannot show. A row is the
 * name, the Inactive tag, where compliance stands, the job title (#699), and up to two places with "+N". Phone, email and Edit are not on it: they
 * are one click away on the Table view, and in the quick look a name opens.
 *
 * IT KEEPS NOTHING. Who is listed, what is chosen and the order all come in; a press goes out. The roster's one filter value
 * stays the only copy (CLAUDE.md hard rule 4).
 *
 * `fills` is the desktop layout: the side is as tall as the box it is given and the rows scroll inside it, so the map beside
 * it stays put. Without it (a tablet, a phone) the side is as tall as its rows and the page scrolls.
 */

export const SHOW_EVERYONE = 'Show everyone';
export const SHOW_NO_PLACE = 'Show them';
export const NOBODY_MATCHES = 'Nobody matches.';
export const NOBODY_MATCHES_HINT = 'Try removing a filter or searching with different words.';
/** Under the rows while a place chosen on the map is what the list shows (frame M2). */
export const SHOW_EVERYONE_HINT = 'To see everyone again, press “Show everyone” or click the shaded area a second time.';
export const SORT_WORDS: Record<NameSort, string> = { asc: 'A to Z', desc: 'Z to A' };

/** "Not on the map: 2 staff with no place yet." With another filter on it says how many of them match: "1 of 2 staff". */
export function notOnTheMapWords(noPlace: { readonly total: number; readonly matching: number }, filtered: boolean): string {
  return `Not on the map: ${filtered ? `${noPlace.matching} of ${noPlace.total}` : String(noPlace.total)} staff with no place yet.`;
}

const useStyles = makeStyles({
  side: { display: 'flex', flexDirection: 'column', minWidth: 0, backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusXLarge, boxShadow: tokens.shadowCard, overflow: 'hidden' },
  sideFills: { height: '100%', minHeight: 0, boxSizing: 'border-box' },
  top: { flexShrink: 0, display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, padding: tokens.spacingHorizontalL, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  // Frames M1 and M2. "Show everyone" is beside the title (the side 400 wide), and under it when the side is the narrow one (360, `narrow`).
  heading: { flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalS, paddingBlock: tokens.spacingVerticalM, paddingInline: tokens.spacingHorizontalL, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  headingNarrow: { flexDirection: 'column', alignItems: 'flex-start' },
  headingLead: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, minWidth: 0 },
  headingWords: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS, minWidth: 0 },
  headingTitle: { margin: 0, fontSize: tokens.fontSizeBase400, lineHeight: tokens.lineHeightBase400, fontWeight: tokens.fontWeightSemibold, overflowWrap: 'anywhere' },
  quiet: { color: tokens.colorNeutralForeground3 },
  sort: { flexShrink: 0, minWidth: 'auto', whiteSpace: 'nowrap', paddingInline: tokens.spacingHorizontalXS, fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  showEveryone: { flexShrink: 0, whiteSpace: 'nowrap' },
  // The rows scroll here, inside the side, when the side fills its box. The keyboard can reach the box itself, so it can be scrolled without a mouse.
  scroll: { flexGrow: 1, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: 'auto', ':focus-visible': { outline: `${tokens.strokeWidthThick} solid ${tokens.colorStrokeFocus2}`, outlineOffset: `calc(0rem - ${tokens.strokeWidthThick})` } },
  rows: { listStyleType: 'none', margin: 0, padding: 0 },
  // Frame M1: padding 10 and 16; the name and compliance on the first line, the job title on the second (#699), the places on the third.
  row: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, paddingBlock: tokens.spacingVerticalMNudge, paddingInline: tokens.spacingHorizontalL, borderBottom: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}` },
  rowHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalXXS },
  rowName: { flexGrow: 1, flexShrink: 1, minWidth: 0 },
  jobTitle: { color: tokens.colorNeutralForeground2, overflowWrap: 'anywhere' },
  rowVerdict: { flexShrink: 0, marginInlineStart: 'auto' },
  hint: { paddingBlock: tokens.spacingVerticalM, paddingInline: tokens.spacingHorizontalL, color: tokens.colorNeutralForeground3 },
  none: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: tokens.spacingVerticalS, padding: tokens.spacingHorizontalL },
  foot: {
    flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', columnGap: tokens.spacingHorizontalM, minHeight: shellMetrics.fieldHeight, boxSizing: 'border-box',
    paddingBlock: tokens.spacingVerticalXXS, paddingInline: tokens.spacingHorizontalL, backgroundColor: tokens.colorNeutralBackground2, borderTop: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground2,
  },
  // The theme gives every Fluent Button the control height; this one is a word in a line of words, so it is only as high as the line allows. A phone keeps the touch size.
  footButton: { minWidth: 'auto', paddingInline: tokens.spacingHorizontalXS, color: tokens.colorBrandForeground1, fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap', [shellMedia.phone]: { minHeight: shellMetrics.touchTarget } },
});

function JobTitle({ title }: { readonly title: string | null }) {
  const styles = useStyles();
  return title === null ? null : <Caption1 className={styles.jobTitle} data-job-title="">{title}</Caption1>;
}

export interface StaffSideListProps extends Pick<StaffTableProps, 'people' | 'entryOf' | 'letterOf' | 'chosenPlaces' | 'onOpen' | 'profileHref'> {
  /**
   * The job title a row shows under the name, worked out by the page from the job profiles the profile shows. `null` is "not known yet"
   * and the row has no such line; a person with no job profile is given the words for that.
   */
  readonly titleOf?: ((person: StaffTableProps['people'][number]) => string | null) | undefined;
  /** Search and the Filters button, and any line of words about the filters under them. */
  readonly top: ReactNode;
  /** What the list is showing: "Staff · 10", "Staff in Alexandria · 3". */
  readonly heading: StaffHeading;
  readonly sort: NameSort;
  readonly onSort: (next: NameSort) => void;
  /** "Show everyone": the place selection goes. */
  readonly onShowEveryone: () => void;
  /** With nobody listed: every filter goes. */
  readonly onClearFilters: () => void;
  /** Staff with no place at all: everyone on the roster, and those of them the other filters match. */
  readonly noPlace: { readonly total: number; readonly matching: number };
  /** Whether a filter other than the place selection is on, so the foot line counts "1 of 2". */
  readonly filtered: boolean;
  /** "Show them": the list becomes the staff with no place. Left out, or with the list already showing them, the foot line has no button. */
  readonly onShowNoPlace?: (() => void) | undefined;
  readonly fills: boolean;
  /** The side is its narrow width (frame M4b, 360): "Show everyone" goes under the title, as drawn, whatever the length of the place's name. */
  readonly narrow?: boolean;
  readonly className?: string;
}

export function StaffSideList({ people, entryOf, letterOf, titleOf, chosenPlaces = [], onOpen, profileHref, top, heading, sort, onSort, onShowEveryone, onClearFilters, noPlace, filtered, onShowNoPlace, fills, narrow = false, className }: StaffSideListProps) {
  const styles = useStyles();
  const flipped: NameSort = sort === 'asc' ? 'desc' : 'asc';
  const rows = people.length === 0 ? (
    <div className={styles.none}>
      <Text weight="semibold">{NOBODY_MATCHES}</Text>
      <Caption1 className={styles.quiet}>{NOBODY_MATCHES_HINT}</Caption1>
      <Button onClick={onClearFilters}>Clear all filters</Button>
    </div>
  ) : (
    <>
      <ul className={styles.rows} aria-label="Staff">
        {people.map((person) => (
          <li key={person.id} className={styles.row}>
            <div className={styles.rowHead}>
              <div className={styles.rowName}><NameLine person={person} onOpen={onOpen} profileHref={profileHref} compact /></div>
              {complianceAndDocumentsShown() ? <div className={styles.rowVerdict}><Compliance person={person} entryOf={entryOf} /></div> : null}
            </div>
            <JobTitle title={titleOf?.(person) ?? null} />
            <PlacesLine person={person} letterOf={letterOf} chosenPlaces={chosenPlaces} />
          </li>
        ))}
      </ul>
      {heading.placeId === null ? null : <Caption1 as="p" block className={styles.hint}>{SHOW_EVERYONE_HINT}</Caption1>}
    </>
  );
  return (
    <section className={mergeClasses(styles.side, fills && styles.sideFills, className)} aria-label="Roster" data-roster-side="">
      <div className={styles.top}>{top}</div>
      <div className={mergeClasses(styles.heading, narrow && heading.showEveryone && styles.headingNarrow)}>
        <div className={styles.headingLead}>
          {heading.placeId === null ? null : <PinLetter letter={letterOf?.(heading.placeId) ?? null} />}
          <div className={styles.headingWords}>
            <h2 className={styles.headingTitle} aria-live="polite">{heading.title}</h2>
            <Caption1 className={styles.quiet}>{heading.subtitle}</Caption1>
          </div>
        </div>
        {heading.showEveryone ? (
          <Button className={styles.showEveryone} onClick={onShowEveryone}>{SHOW_EVERYONE}</Button>
        ) : (
          <Button size="small" appearance="transparent" className={styles.sort} iconPosition="after" icon={sort === 'asc' ? <ShellIcons.chevronUp /> : <ShellIcons.chevronDown />} aria-label={`Sorted ${SORT_WORDS[sort]}. Sort names ${SORT_WORDS[flipped]}`} onClick={() => onSort(flipped)}>{SORT_WORDS[sort]}</Button>
        )}
      </div>
      {fills ? <div className={styles.scroll} tabIndex={0} role="group" aria-label="Staff list" data-roster-rows="">{rows}</div> : rows}
      {noPlace.total === 0 ? null : (
        <div className={styles.foot} data-roster-foot="">
          <Caption1>{notOnTheMapWords(noPlace, filtered)}</Caption1>
          {onShowNoPlace === undefined ? null : <Button size="small" appearance="transparent" className={styles.footButton} onClick={onShowNoPlace}>{SHOW_NO_PLACE}</Button>}
        </div>
      )}
    </section>
  );
}
